import type { DialogProps } from "@mui/material";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
} from "@mui/material";
import type { ReactNode } from "react";
import styled from "styled-components";

import CloseIcon from "@/assets/close.svg";

const StyledCloseButton = styled.img`
    height: 9px;
    width: 9px;
`;

interface ModalProps extends Omit<DialogProps, "open" | "title"> {
    /**
     * Whether the modal is open.
     */
    visible?: boolean;
    /**
     * The close event handler.
     */
    onClose?: () => void;
    /**
     * The modal body content.
     */
    children: ReactNode;
    /**
     * The footer content.
     */
    footer?: ReactNode;
    /**
     * The modal title.
     */
    title: string;
}

/**
 * MUI Dialog wrapper with title, content, footer, and close button.
 *
 * @param {ModalProps} props - The props for the Modal component.
 * @returns {ReactNode} The rendered modal component.
 */
const Modal = ({
    visible = false,
    onClose = () => null,
    children,
    footer = undefined,
    title,
    slotProps = undefined,
    ...props
}: ModalProps): ReactNode => {
    const close = () => {
        if (onClose) onClose();
    };

    const paperProps =
        typeof slotProps?.paper === "function" ? {} : slotProps?.paper;

    const mergedSlotProps = {
        ...slotProps,
        paper: {
            ...paperProps,
            sx: {
                borderRadius: "2px",
                ...paperProps?.sx,
            },
        },
    };

    return (
        <Dialog
            open={visible}
            onClose={close}
            slotProps={mergedSlotProps}
            {...props}
        >
            <DialogTitle
                sx={{
                    borderBottom: "1px solid #ebebeb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "rgb(72, 88, 101)",
                }}
            >
                <Box component="span">{title}</Box>
                {onClose && (
                    <IconButton
                        onClick={close}
                        size="small"
                        aria-label="close"
                        sx={{ p: 0.5 }}
                    >
                        <StyledCloseButton src={CloseIcon} />
                    </IconButton>
                )}
            </DialogTitle>
            <DialogContent
                sx={{
                    padding: "0",
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                }}
            >
                {children}
            </DialogContent>
            {!!footer && (
                <DialogActions sx={{ borderTop: "1px solid #ebebeb" }}>
                    {footer}
                </DialogActions>
            )}
        </Dialog>
    );
};

export default Modal;
